import { useGetQSetSimilarityWithQuestionsByIdQuery } from '@exam-management/graphql';
import {
  useGetQSetQuestionsWithImage,
  type QSetQuestionWithQuestion,
} from '@exam-management/qset-management';
import { ErrorBox, Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState, useEffect } from 'react';

import type { QSetSimilarityCheckResultDetailsContextData } from './types';

export const QSetSimilarityCheckResultDetailsContext =
  createContext<QSetSimilarityCheckResultDetailsContextData | null>(null);

interface QSetSimilarityCheckResultDetailsProviderProps extends PropsWithChildren {
  comparisonId: string;
  resultId: string;
}

export default function QSetSimilarityCheckResultDetailsProvider(
  props: Readonly<QSetSimilarityCheckResultDetailsProviderProps>,
) {
  const { comparisonId, resultId, children } = props;

  const [duplicatedQuestionsWithImage, setDuplicatedQuestionsWithImage] = useState<
    QSetQuestionWithQuestion[]
  >([]);

  const {
    data,
    loading: isGettingQSetSimilarityWithQuestions,
    error,
  } = useGetQSetSimilarityWithQuestionsByIdQuery({
    variables: { input: { id: resultId, resultId: comparisonId } },
  });

  const { getQSetQuestionsWithImage, loading: isGettingQSetQuestionsWithImage } =
    useGetQSetQuestionsWithImage();

  const isLoading = isGettingQSetSimilarityWithQuestions || isGettingQSetQuestionsWithImage;

  const qSetSimilarityResult = useMemo(
    () => data?.excGetQSetSimilarityWithQuestionsById?.qSetSimilarityResult,
    [data],
  );

  const duplicatedQuestions = useMemo(
    () => [...(qSetSimilarityResult?.duplicatedQuestions ?? [])].sort((a, b) => a.order - b.order),
    [qSetSimilarityResult],
  );

  useEffect(() => {
    const getDuplicatedQSetQuestionsWithImage = async () => {
      const qSetQuestionsWithImage = await getQSetQuestionsWithImage(duplicatedQuestions);
      setDuplicatedQuestionsWithImage(qSetQuestionsWithImage);
    };

    getDuplicatedQSetQuestionsWithImage();
  }, [duplicatedQuestions, getQSetQuestionsWithImage]);

  const contextValue = useMemo(
    () => ({
      qSetACode: qSetSimilarityResult?.qSetACode ?? '',
      qSetBCode: qSetSimilarityResult?.qSetBCode ?? '',
      totalDuplicatedQuestions: duplicatedQuestions.length,
      duplicatedQuestions: duplicatedQuestionsWithImage,
    }),
    [qSetSimilarityResult, duplicatedQuestionsWithImage],
  );

  if (isLoading) {
    return <Loading height='100%' />;
  }

  if (error) {
    return <ErrorBox />;
  }

  return (
    <QSetSimilarityCheckResultDetailsContext.Provider value={contextValue}>
      {children}
    </QSetSimilarityCheckResultDetailsContext.Provider>
  );
}
